/**
 * iter140 — Admin Face-Match Debug Search page.
 *
 * Route: /gallery/:eventId/debug-search
 *
 * Uploads one selfie, runs POST /gallery/events/{id}/debug-search, and
 * renders EVERY diagnostic signal so the admin can immediately see why
 * a search returned 0 matches. Never shown to guests.
 */
import { useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Upload, Loader2, AlertCircle, CheckCircle2, XCircle, ArrowLeft, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils";

function Row({ label, value, tone }) {
  const toneCls = tone === "ok"
    ? "text-emerald-800 bg-emerald-50 border-emerald-200"
    : tone === "bad"
      ? "text-rose-800 bg-rose-50 border-rose-200"
      : "text-stone-800 bg-stone-50 border-stone-200";
  return (
    <div className={`px-3 py-1.5 rounded border text-[12px] flex justify-between items-center gap-3 ${toneCls}`}>
      <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
      <span className="font-mono text-[12px] truncate">{String(value)}</span>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <section className="bg-white rounded-lg border border-stone-200 p-4 space-y-2" data-testid={testid}>
      <h3 className="text-sm font-semibold text-stone-900">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">{children}</div>
    </section>
  );
}

const VERDICT_TONE = {
  ACCEPTED: "text-emerald-700 bg-emerald-50 border-emerald-200",
  DEEP_ACCEPTED: "text-emerald-700 bg-emerald-50 border-emerald-200",
  BORDERLINE: "text-amber-700 bg-amber-50 border-amber-200",
  LOW_SIM: "text-stone-700 bg-stone-100 border-stone-200",
  RANDOM: "text-stone-500 bg-stone-50 border-stone-200",
  NOT_IN_TOP_50: "text-rose-700 bg-rose-50 border-rose-200",
};

const ROOT_CAUSE_TONE = {
  no_face_in_selfie: "amber",
  preprocessing_mismatch: "rose",
  wrong_scope: "rose",
  gallery_not_indexed: "amber",
  corrupted_vectors: "rose",
  preprocessing_ok_recall_low: "amber",
  threshold_only: "emerald",
  unknown: "stone",
};

export default function GalleryDebugSearch() {
  const { eventId } = useParams();
  const [file, setFile] = useState(null);
  const [expectedPhotoId, setExpectedPhotoId] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("selfie", file);
      if (expectedPhotoId.trim()) fd.append("expected_photo_id", expectedPhotoId.trim());
      const { data } = await api.post(`/gallery/events/${eventId}/debug-search`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }, [file, expectedPhotoId, eventId]);

  const rc = result?.root_cause;
  const rcTone = rc ? ROOT_CAUSE_TONE[rc.issue_id] || "stone" : "stone";
  const rcCls = rcTone === "emerald"
    ? "bg-emerald-50 text-emerald-900 border-emerald-200"
    : rcTone === "amber"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : rcTone === "rose"
        ? "bg-rose-50 text-rose-900 border-rose-200"
        : "bg-stone-50 text-stone-900 border-stone-200";

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5" data-testid="debug-search-page">
      <div className="flex items-center gap-2 text-sm text-stone-500">
        <Link to={`/gallery/${eventId}`} className="inline-flex items-center gap-1 hover:text-stone-900">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to gallery
        </Link>
      </div>
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-rose-700" strokeWidth={1.6} />
        <h1 className="text-xl font-bold text-stone-900">Find My Photos · Debug Search</h1>
        <span className="text-[10px] uppercase tracking-wider bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">Admin only</span>
      </div>
      <p className="text-sm text-stone-600 max-w-2xl">
        Upload a selfie and (optionally) the ID of a photo you expect to be found.
        You'll see the full end-to-end pipeline: face detection, embedding norms,
        gallery scope, top-50 candidates with cosine scores, and a root-cause verdict.
      </p>

      {/* Form */}
      <form onSubmit={onSubmit} className="bg-white rounded-lg border border-stone-200 p-4 space-y-3" data-testid="debug-search-form">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-stone-500 font-medium block mb-1.5">
            Selfie (JPEG/PNG)
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-stone-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200"
            data-testid="debug-selfie-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-stone-500 font-medium block mb-1.5">
            Expected photo ID <span className="text-stone-400 lowercase">(optional — reveals rank & score)</span>
          </label>
          <input
            type="text"
            value={expectedPhotoId}
            onChange={(e) => setExpectedPhotoId(e.target.value)}
            placeholder="e.g., f3fd7527-f737-444e-a2a0-d621fec4b41f"
            className="w-full font-mono text-sm border border-stone-200 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-500"
            data-testid="debug-expected-input"
          />
        </div>
        <button
          type="submit"
          disabled={!file || busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-rose-700 text-white text-sm font-semibold hover:bg-rose-800 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="debug-run-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Run diagnostic
        </button>
        {err && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-1.5" data-testid="debug-error">
            {err}
          </div>
        )}
      </form>

      {/* Result */}
      {result && (
        <>
          {/* Root cause banner */}
          <div className={`rounded-lg border p-4 ${rcCls}`} data-testid="debug-root-cause">
            <div className="flex items-center gap-2 mb-1">
              {rc.issue_id === "threshold_only" ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span className="font-semibold text-sm">Root cause: {rc.issue_id.replace(/_/g, " ")}</span>
              <span className="text-[10px] uppercase tracking-wider opacity-70">
                confidence: {rc.confidence}
              </span>
            </div>
            <p className="text-[13px] leading-relaxed" data-testid="debug-root-cause-recommendation">{rc.recommendation}</p>
            {rc.evidence?.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-[11px] font-mono opacity-80">
                {rc.evidence.map((e, i) => (
                  <li key={i}>· {e}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Stage 1: Selfie */}
          <Section title="Stage 1 · Selfie Analysis" testid="debug-stage1">
            <Row label="Faces detected" value={result.stage1_selfie.selfie_faces_detected}
                 tone={result.stage1_selfie.selfie_faces_detected > 0 ? "ok" : "bad"} />
            <Row label="Detection trace" value={result.stage1_selfie.selfie_detection_trace || "—"} />
            <Row label="Image size" value={result.stage1_selfie.selfie_image_size || "—"} />
            <Row label="Embedding dim" value={result.stage1_selfie.selfie_embedding_dim || 0}
                 tone={result.stage1_selfie.selfie_embedding_dim === 512 ? "ok" : "bad"} />
            <Row label="Embedding count" value={result.stage1_selfie.selfie_embedding_count || 0} />
            <Row label="Embedding L2 norms" value={JSON.stringify(result.stage1_selfie.selfie_embedding_norms || [])}
                 tone={(result.stage1_selfie.selfie_embedding_norms || []).every(n => n > 0.98 && n < 1.02) ? "ok" : "bad"} />
          </Section>

          {/* Stage 2: Gallery */}
          <Section title="Stage 2 · Gallery Scope" testid="debug-stage2">
            <Row label="Scope event IDs" value={(result.stage2_gallery.scope_event_ids || []).length} />
            <Row label="Total photos in scope" value={result.stage2_gallery.total_photos_in_scope || 0} />
            <Row label="Indexed photos" value={result.stage2_gallery.indexed_photos || 0}
                 tone={(result.stage2_gallery.indexed_photos || 0) > 0 ? "ok" : "bad"} />
            <Row label="Faces total (gallery)" value={result.stage2_gallery.faces_total_across_gallery || 0}
                 tone={(result.stage2_gallery.faces_total_across_gallery || 0) > 0 ? "ok" : "bad"} />
            <Row label="Photos scored" value={result.stage2_gallery.photos_scored || 0} />
            <Row label="Embedding dim histogram" value={JSON.stringify(result.stage2_gallery.embedding_dim_histogram || {})} />
          </Section>

          {/* Stage 3: Pipeline verification */}
          <Section title="Stage 3 · Pipeline Verification (indexing vs search)" testid="debug-stage3">
            <Row label="Embedding model" value={result.stage3_pipeline_verification.embedding_model} />
            <Row label="Detection pipeline" value={result.stage3_pipeline_verification.detection_pipeline} />
            <Row label="Dim consistent" value={result.stage3_pipeline_verification.dim_consistent ? "yes" : "no"}
                 tone={result.stage3_pipeline_verification.dim_consistent ? "ok" : "bad"} />
            <Row label="Selfie normalised" value={result.stage3_pipeline_verification.selfie_normalised ? "yes" : "no"}
                 tone={result.stage3_pipeline_verification.selfie_normalised ? "ok" : "bad"} />
            <Row label="Gallery normalised" value={result.stage3_pipeline_verification.gallery_normalised ? "yes" : "no"}
                 tone={result.stage3_pipeline_verification.gallery_normalised ? "ok" : "bad"} />
            <Row label="Vector store" value={result.stage3_pipeline_verification.vector_store_collection} />
            <Row label="Gallery sample norms" value={JSON.stringify(result.stage3_pipeline_verification.gallery_sample_norms || [])} />
            <Row label="Same pipeline both sides" value={result.stage3_pipeline_verification.same_pipeline_for_indexing_and_search ? "yes" : "no"}
                 tone={result.stage3_pipeline_verification.same_pipeline_for_indexing_and_search ? "ok" : "bad"} />
          </Section>

          {/* Expected photo diagnosis */}
          {result.expected_photo_diagnosis && (
            <Section title="Expected Photo Diagnosis" testid="debug-expected">
              {Object.entries(result.expected_photo_diagnosis).map(([k, v]) => (
                <Row key={k} label={k.replace(/_/g, " ")} value={v == null ? "—" : String(v)}
                     tone={typeof v === "boolean" ? (v ? "ok" : "bad") : undefined} />
              ))}
            </Section>
          )}

          {/* Optimal threshold */}
          <Section title="Threshold Analysis" testid="debug-threshold">
            <Row label="Current strict" value={result.optimal_threshold.current_strict_threshold} />
            <Row label="Current deep" value={result.optimal_threshold.current_deep_threshold} />
            <Row label="Recommended strict" value={result.optimal_threshold.recommended_strict_threshold} />
            <Row label="Recommended deep" value={result.optimal_threshold.recommended_deep_threshold} />
            <div className="col-span-full text-[11px] text-stone-500 italic mt-1">{result.optimal_threshold.note}</div>
          </Section>

          {/* Top-50 candidates */}
          <section className="bg-white rounded-lg border border-stone-200 p-4 space-y-2" data-testid="debug-top50">
            <h3 className="text-sm font-semibold text-stone-900">Top 50 Candidates (before threshold)</h3>
            <p className="text-[11px] text-stone-500">
              Every photo scored, sorted by cosine similarity. Green = accepted (≥ 0.30 strict or ≥ 0.24 deep).
            </p>
            <div className="overflow-x-auto border border-stone-200 rounded">
              <table className="w-full text-[12px] font-mono">
                <thead className="bg-stone-50 text-stone-500 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-1.5 text-left">#</th>
                    <th className="px-2 py-1.5 text-left">Filename</th>
                    <th className="px-2 py-1.5 text-right">Cosine</th>
                    <th className="px-2 py-1.5 text-right">Faces</th>
                    <th className="px-2 py-1.5 text-center">Verdict</th>
                    <th className="px-2 py-1.5 text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.top50_candidates.map((r, i) => (
                    <tr key={r.photo_id} className="border-t border-stone-100" data-testid={`debug-top50-row-${i}`}>
                      <td className="px-2 py-1 text-stone-500">{i + 1}</td>
                      <td className="px-2 py-1 truncate max-w-[220px]" title={r.photo_id}>{r.filename}</td>
                      <td className={`px-2 py-1 text-right ${r.accepted ? "text-emerald-700 font-semibold" : ""}`}>{r.cosine_similarity.toFixed(4)}</td>
                      <td className="px-2 py-1 text-right text-stone-500">{r.face_count}</td>
                      <td className="px-2 py-1 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] ${VERDICT_TONE[r.verdict] || "border-stone-200"}`}>
                          {r.verdict}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-stone-500 text-[11px]">{r.rejection_reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
